import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { z } from 'zod'
import { Alert, Badge, Box, Button, Group, Stack, Tabs, Title } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { useSystemDetails } from '@features/systems/hooks/useSystemDetails'
import { SystemHeader } from '@features/systems/components/SystemHeader'
import { cadwellApplicationHistoryQueryOptions, systemQueryOptions } from '@api/systems/systemQueries'
import { systemWorkItemsQueryOptions } from '@api/incidents/incidentIntegrationQueries'
import { systemLicensesBySystemIdQueryOptions } from '@api/systems/systemLicensesQueries'
import { ingestionHistoryQueryOptions, systemCustomerAssociationsQueryOptions } from '@api/customers/customerQueries'
import { useEffect } from 'react'
import { SystemOverviewTab } from "@features/systems/components/SystemOverviewTab.tsx"
import { UploadedArtifactsTable } from "@features/systems/components/artifact-management/UploadedArtifactsTable.tsx"
import { CadLinkTopologyTab } from "@features/systems/components/cadlink-topology/CadLinkTopologyTab.tsx"
import { useNewArtifactTracking } from '@features/systems/hooks/useNewArtifactTracking'
import { UserRole } from "@shared-types/auth.types.ts";
import { ProtectedComponent } from "@components/ProtectedComponent.tsx";

/**
 * Search parameters schema for the system details route
 * @param customerId - Optional customer ID for navigation context
 */
const SearchSchema = z.object({
  customerId: z.string().nullish(),
  tab: z.enum(['overview', 'files', 'topology']).optional().catch('overview'),
})

export const Route = createFileRoute('/_authed/systems/$systemId/')({
  validateSearch: SearchSchema,
  loader: ({context: {queryClient}, params: {systemId}}) => {
    queryClient.prefetchQuery(systemQueryOptions(systemId))
    queryClient.prefetchQuery(systemWorkItemsQueryOptions(systemId))
    queryClient.prefetchQuery(systemCustomerAssociationsQueryOptions(systemId))
    queryClient.prefetchQuery(systemLicensesBySystemIdQueryOptions(systemId))
    queryClient.prefetchQuery(cadwellApplicationHistoryQueryOptions(systemId))
    queryClient.prefetchQuery(ingestionHistoryQueryOptions())
    return {}
  },
  errorComponent: SystemDetailsErrorComponent,
  component: SystemDetailsPage,
})

/**
 * Main system details page displaying comprehensive system information in tabbed interface.
 * Serves as the primary landing page for individual system management and monitoring.
 */
function SystemDetailsPage() {
  const {systemId} = Route.useParams()
  const {isSignalRConnected} = useSystemDetails(systemId)
  const {badgeArtifactIds, pillArtifactIds, clearBadge, clearPills} = useNewArtifactTracking(systemId)
  const {tab: activeTab = 'overview', customerId} = Route.useSearch()
  const navigate = Route.useNavigate()

  // Clear badge when switching to files tab, clear pills when leaving
  const handleTabChange = (value: string | null) => {
    // Clear pills when leaving files tab
    if (activeTab === 'files' && value !== 'files') {
      clearPills()
    }

    navigate({search: (prev) => ({...prev, tab: value === 'overview' ? undefined : value})})

    // Clear badge when entering files tab
    if (value === 'files') {
      clearBadge()
    }
  }

  return (
    <Stack gap={0}>
      <SystemHeader
        systemId={systemId}
        isConnected={isSignalRConnected}
        isLoading={false}
      />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        style={{flex: 1}}
        pl='2.375rem'
        orientation="horizontal"
      >
        <Box
          pt="md"
          bg='var(--mantine-color-body)'
          pos='sticky'
          top='calc(var(--app-shell-header-offset, 0rem) + var(--page-header-height, 0px))'
          style={{zIndex: 98}}
          mx={-10} // the margin/padding counteracting here is to account for the tab content box shadows as we scroll (they would previously appear)
          px={10}>
          <Tabs.List>
            <Tabs.Tab value="overview" aria-label="system-overview-tab">
              <Title fw={400}>
                Overview
              </Title>
            </Tabs.Tab>

            <ProtectedComponent requiredRole={UserRole.Support}>
              <Tabs.Tab value="files" aria-label="system-files-tab">
                <Group gap="xs">
                  <Title fw={400}>
                    Files
                  </Title>
                  {badgeArtifactIds.size > 0 && (
                    <Badge color="red" size="sm">+{badgeArtifactIds.size}</Badge>
                  )}
                </Group>
              </Tabs.Tab>
            </ProtectedComponent>
            <Tabs.Tab value="topology" aria-label="system-topology-tab">
              <Title fw={400}>
                Topology
              </Title>
            </Tabs.Tab>
          </Tabs.List>
        </Box>

        <Tabs.Panel value="overview" pt="lg" h="100%">
          <SystemOverviewTab
            systemId={systemId}
            isConnected={isSignalRConnected}
          />
        </Tabs.Panel>

        <ProtectedComponent requiredRole={UserRole.Support}>
          <Tabs.Panel value="files" pt="md" h="100%">
            <UploadedArtifactsTable systemId={systemId} newArtifactIds={pillArtifactIds}/>
          </Tabs.Panel>
        </ProtectedComponent>

        <Tabs.Panel value="topology" pt="md" h="100%">
          <CadLinkTopologyTab systemId={systemId} customerId={customerId}/>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}

/**
 * Error component props for system details page
 * @param error - The error object containing details about what went wrong
 */
/**
 * Error boundary component for system details page failures.
 * Provides user-friendly error display with retry and navigation options.
 */
function SystemDetailsErrorComponent({error}: { error: Error }) {
  const router = useRouter()
  const queryErrorResetBoundary = useQueryErrorResetBoundary()

  useEffect(() => {
    queryErrorResetBoundary.reset()
  }, [queryErrorResetBoundary])

  return (
    <Alert
      icon={<IconInfoCircle size={16}/>}
      title="Error Loading System"
      color="red"
      variant="light"
    >
      {error.message || 'Unable to load system details. Please try again.'}
      <Group mt="md">
        <Button variant="light" onClick={() => router.invalidate()}>
          Retry
        </Button>
        <Button variant="outline" onClick={() => router.navigate({to: '/admin/systems'})}>
          Return to System List
        </Button>
      </Group>
    </Alert>
  )
}